import { getCurationContentCentres } from '../../helpers/curation-storefront'
import { FulfilmentCentre } from '../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { ArrowDown, ArrowUp, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  useReorderListMenuArticlesMutation,
  useUpdateListMenuMutation,
} from '../../redux-toolkits/list-menu/listMenu.slice'
import type { ListMenuItem } from '../../redux-toolkits/list-menu/listMenu.types'
import type { Article } from '../../redux-toolkits/lists/list.types'
import { Button } from '../button'
import { SelectGoodListModal } from '../modal/app_modals/selectGoodListModal'
import { TextSubtext } from '../typography/textSubtext'

type Props = {
  menu: ListMenuItem
  fulfilmentCentres?: FulfilmentCentre[]
  onRefetch: () => void
  onChange?: (articles: ListMenuItem['articles']) => void
}

export const NavMenuArticles = ({
  menu,
  onRefetch,
  onChange,
  fulfilmentCentres,
}: Props) => {
  const contentCentres = getCurationContentCentres(fulfilmentCentres)
  const contentCentreId = contentCentres?.[0]?._id
  const [orderedArticles, setOrderedArticles] = useState(menu.articles ?? [])
  const [addModalOpen, setAddModalOpen] = useState(false)

  const [reorderListMenuArticles, { isLoading: isReordering }] =
    useReorderListMenuArticlesMutation()
  const [updateListMenu, { isLoading: isUpdating }] =
    useUpdateListMenuMutation()

  const isBusy = !onChange && (isReordering || isUpdating)

  useEffect(() => {
    setOrderedArticles(menu.articles ?? [])
  }, [menu.articles])

  const persistReorder = async (articles: typeof orderedArticles) => {
    if (onChange) {
      onChange(articles)
      return
    }
    await reorderListMenuArticles({
      listMenuId: menu._id,
      articleIds: articles.map((a) => a._id),
    })
    onRefetch()
  }

  const persistUpdate = async (articles: typeof orderedArticles) => {
    if (onChange) {
      onChange(articles)
      return
    }

    await updateListMenu({
      id: menu._id,
      body: { articles: articles.map((a) => a._id) },
    })
    onRefetch()
  }

  const handleMoveUp = (index: number) => {
    if (index === 0) return
    const next = [...orderedArticles]
    ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
    setOrderedArticles(next)
    persistReorder(next)
  }

  const handleMoveDown = (index: number) => {
    if (index === orderedArticles.length - 1) return
    const next = [...orderedArticles]
    ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
    setOrderedArticles(next)
    persistReorder(next)
  }

  const handleRemove = (index: number) => {
    if (orderedArticles.length <= 1) return
    const next = orderedArticles.filter((_, i) => i !== index)
    setOrderedArticles(next)
    persistUpdate(next)
  }

  const handleAdd = (selected: Article[]) => {
    const existingIds = new Set(orderedArticles.map((a) => a._id))
    const newEntries = selected
      .filter((a) => !existingIds.has(a._id))
      .map((a) => ({ _id: a._id, title: a.title }))
    const next = [...orderedArticles, ...newEntries]
    setOrderedArticles(next)
    persistUpdate(next)
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <TextSubtext text="Good Lists" subtext="Add at least one good list." />

        <Button
          type="button"
          label="Add Good List"
          primary
          size="large"
          onClick={() => setAddModalOpen(true)}
        />
      </div>

      {orderedArticles.length === 0 ? (
        <p className="text-sm text-neutral">No good lists added yet.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-divider-gray">
          {orderedArticles.map((article, index) => (
            <li
              key={article._id}
              className="flex items-center justify-between py-3"
            >
              <span className="text-sm">{article.title}</span>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0 || isBusy}
                  className="disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => handleMoveDown(index)}
                  disabled={index === orderedArticles.length - 1 || isBusy}
                  className="disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ArrowDown size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  disabled={isBusy || orderedArticles.length <= 1}
                  className="disabled:opacity-30 disabled:cursor-not-allowed text-red-500"
                >
                  <X size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <SelectGoodListModal
        fulfilmentCentreId={contentCentreId}
        open={addModalOpen}
        setOpen={setAddModalOpen}
        loading={isBusy}
        onSuccessCallback={(selected) => {
          handleAdd(selected)
          setAddModalOpen(false)
        }}
      />
    </div>
  )
}
